import { useTranslation } from 'react-i18next';

// Components
import { Popover } from '../../../store/popover/PopoverContext';
import { PopoverTrigger, PopoverContent } from '../../atoms/Popover/Popover';
import IconButton from '../../atoms/IconButton/IconButton';
import Checkbox from '../../atoms/Checkbox/Checkbox';
import NavigationSearch from '../../molecules/NavigationSearch/NavigationSearch';
import SearchField from '../../molecules/SearchField/SearchField';
import { CustomVueSectionTitle, CustomVueSectionContent } from '../../atoms/CustomVueSection/CustomVueSection';
import ColumnsVisibility from '../../molecules/ColumnsVisibility/ColumnsVisibility';
import SearchGroupList, { SearchGroupListLabel } from '../../molecules/SearchGroupList/SearchGroupList';
import SearchListItem from '../../molecules/SearchListItem/SearchListItem';
import SearchColumnGroup, { SearchColumnGroupList } from '../SearchColumnsGroup/SearchColumnGroup';
import Badge from '../../atoms/Badge/Badge';

/**
 * Filter Navigation Search component
 * @param {object} params
 * @param {import('../../../domain/tableSearch/useTableSearch').useTableSearchReturn} params.tableSearchDomain
 * @param {string} params.searchValue
 * @param {Array<ApiManagerTableFiltersSearchOptions>} params.options
 * @param {boolean} params.isLoading
 * @param {() => void} params.handleSearchMutation
 * @returns {React.ReactNode} Filter Navigation Search component
 */
function FilterNavigationSearch({
  tableSearchDomain,
  searchValue,
  options,
  isLoading,
  handleSearchMutation,
}) {
  const { t } = useTranslation();
  const {
    searchOptionFiltered,
    tableSearch,
    setTableSearch,

    // Table Search - Search
    searchColumnsGroups,
    handleSearchColumnsGroups,

    // Table Search - Popover
    popoverSearchColumnsGroupsIsOpen,
    setPopoverSearchColumnsGroupsIsOpen,

    // Table Search - Selected columns
    selectedColumns,
    handleSelectColumn,
    handleSelectGroupColumns,
    areAllColumnsGroupSelected,

    handleSelectAllColumns,
    areAllColumnsSelected,
    isAllColumnsSelectionIndeterminate,
    isColumnsGroupSelectionIndeterminate,
    tableColumnsCount,
  } = tableSearchDomain;

  return (
    <NavigationSearch
      id="table-search"
      isMenuOpen
      placeholder={t('table.search')}
      onFocus={() => {}}
      value={tableSearch}
      defaultValue={searchValue}
      onChange={(e) => setTableSearch(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          handleSearchMutation();
        }
      }}
      onClear={() => setTableSearch('')}
      theme="form"
      isLoading={isLoading}
      hasIcon={false}
    >
      {/* Search Columns Groups Popover */}
      <>
        <Popover
          open={popoverSearchColumnsGroupsIsOpen}
          onOpenChange={setPopoverSearchColumnsGroupsIsOpen}
          placement="bottom-start"
        >
          <PopoverTrigger asChild>
            {options?.length > 0 ? (
              <IconButton
                icon="setting-5"
                color="neutral200"
                size="medium"
                onClick={() => setPopoverSearchColumnsGroupsIsOpen(
                  !popoverSearchColumnsGroupsIsOpen,
                )}
                disabled={isLoading}
              />
            ) : <div />}
          </PopoverTrigger>
          <PopoverContent>
            <SearchColumnGroup>
              <CustomVueSectionTitle>
                {t('table.filters.columnsGroups')}
              </CustomVueSectionTitle>
              <CustomVueSectionContent>
                <SearchField
                  id="table-columns-groups-search"
                  placeholder={t('table.filters.columnsGroupsSearch')}
                  onFocus={() => {}}
                  disabled={false}
                  onChange={(e) => handleSearchColumnsGroups(e.target.value)}
                  value={searchColumnsGroups}
                />
              </CustomVueSectionContent>
              <CustomVueSectionContent>
                <Checkbox
                  id="select-all-columns"
                  checked={areAllColumnsSelected(
                    tableColumnsCount(options),
                  )}
                  indeterminate={isAllColumnsSelectionIndeterminate(
                    tableColumnsCount(options),
                  )}
                  onChange={() => handleSelectAllColumns(options)}
                  size="small"
                  theme="black"
                  label={t('table.filters.selectAllColumns')}
                />
              </CustomVueSectionContent>
              <SearchColumnGroupList>
                <ColumnsVisibility
                  options={searchOptionFiltered}
                  messageNoOptions={t('table.filters.noResult')}
                >
                  {(label, groupOptions) => (
                    <SearchGroupList
                      key={label}
                      hasLabel={!!label}
                      items={groupOptions}
                      action={(
                        <Checkbox
                          id={`select-all-columns-${label}`}
                          checked={areAllColumnsGroupSelected(
                            groupOptions.map((option) => option.id),
                          )}
                          indeterminate={isColumnsGroupSelectionIndeterminate(
                            groupOptions.map((option) => option.id),
                          )}
                          onChange={() => handleSelectGroupColumns(
                            groupOptions.map((option) => option.id),
                          )}
                          size="small"
                          theme="black"
                          label={(
                            <SearchGroupListLabel>
                              {label}
                            </SearchGroupListLabel>
                          )}
                        />
                      )}
                    >
                      {(column) => (
                        <SearchListItem
                          key={column.id}
                          id={column.id}
                          isDisabled
                        >
                          <Checkbox
                            id={`select-column-${column.id}`}
                            checked={selectedColumns[column.id]}
                            onChange={() => handleSelectColumn(column.id)}
                            size="small"
                            theme="black"
                            label={
                          column.lib
                        }
                          />
                        </SearchListItem>
                      )}
                    </SearchGroupList>
                  )}
                </ColumnsVisibility>
              </SearchColumnGroupList>
            </SearchColumnGroup>
          </PopoverContent>
        </Popover>
        {Object.keys(selectedColumns).length > 0 ? (
          <Badge value={Object.keys(selectedColumns).length} theme="primary" />
        ) : null}

        <IconButton
          icon="search-normal"
          color="primary100"
          size="medium"
          onClick={handleSearchMutation}
          disabled={isLoading}
          isLoading={isLoading}
        />
      </>
    </NavigationSearch>
  );
}

export default FilterNavigationSearch;
